import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BiddingModal from '../components/BiddingModal';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Toaster } from '../components/ui/sonner';
import { getPropertyById } from '../api/api';
import { BedDouble, Users, Wifi, UtensilsCrossed, WashingMachine, Car, CheckCircle, ArrowLeft, Gavel } from 'lucide-react';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showBiddingModal, setShowBiddingModal] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const data = await getPropertyById(id);
        setProperty(data);
      } catch (error) {
        console.error('Error loading property:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  const getAmenityIcon = (amenity) => {
    const lowerAmenity = amenity.toLowerCase();
    if (lowerAmenity.includes('wifi')) return <Wifi size={20} />;
    if (lowerAmenity.includes('kitchen')) return <UtensilsCrossed size={20} />;
    if (lowerAmenity.includes('washing')) return <WashingMachine size={20} />;
    if (lowerAmenity.includes('parking')) return <Car size={20} />;
    return <CheckCircle size={20} />;
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-lg" style={{ color: '#636e72' }}>Loading property details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Property Not Found</h1>
          <Link to="/properties">
            <Button className="btn-primary-custom rounded-full">Back to Properties</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <Navbar />
      <Toaster position="top-center" richColors />
      <BiddingModal 
        property={property} 
        open={showBiddingModal} 
        onClose={() => setShowBiddingModal(false)} 
      />

      {/* Property Details */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <Button 
            data-testid="back-button"
            variant="ghost" 
            onClick={() => navigate(-1)} 
            className="mb-6 hover:bg-transparent"
            style={{ color: '#636e72' }}
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </Button>

          {/* Image Gallery */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div>
              <div className="rounded-2xl overflow-hidden mb-4 shadow-lg">
                <img 
                  src={property.images[selectedImage]} 
                  alt={property.name}
                  className="w-full h-[500px] object-cover"
                  data-testid="main-property-image"
                />
              </div>
              {property.images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {property.images.map((image, index) => (
                    <div 
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`cursor-pointer rounded-xl overflow-hidden ${
                        selectedImage === index ? 'ring-4 ring-[#d4a574]' : 'ring-2 ring-transparent hover:ring-gray-300'
                      } transition-all`}
                      data-testid={`thumbnail-${index}`}
                    >
                      <img 
                        src={image} 
                        alt={`${property.name} ${index + 1}`}
                        className="w-full h-24 object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="inline-block mb-4">
                <span className="badge-premium">Premium Property</span>
              </div>
              <h1 className="text-5xl font-bold mb-6" data-testid="property-title" style={{ color: '#1a1a2e', letterSpacing: '-0.02em' }}>{property.name}</h1>
              
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-6 text-lg">
                  <div className="flex items-center" style={{ color: '#636e72' }}>
                    <BedDouble size={28} className="mr-2" style={{ color: '#d4a574' }} />
                    <span className="font-semibold">{property.bedrooms} Bedrooms, {property.beds} Beds</span>
                  </div>
                  <div className="flex items-center" style={{ color: '#636e72' }}>
                    <Users size={28} className="mr-2" style={{ color: '#d4a574' }} />
                    <span className="font-semibold">Guests: {property.guests}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm" style={{ color: '#636e72' }}>From</p>
                  <p className="text-4xl font-bold" style={{ color: '#d4a574' }}>£{property.nightly_rate}</p>
                  <p className="text-sm" style={{ color: '#636e72' }}>per night</p>
                </div>
              </div>

              {/* Amenities */}
              <Card className="mb-6 border-2 rounded-2xl" style={{ borderColor: '#e8e8e8' }}>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Amenities & Features</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {property.amenities.map((amenity, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition">
                        <div style={{ color: '#d4a574' }}>
                          {getAmenityIcon(amenity)}
                        </div>
                        <span style={{ color: '#2d3436', fontWeight: '500' }}>{amenity}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-3">
                <Button 
                  data-testid="place-bid-button" 
                  size="lg" 
                  onClick={() => setShowBiddingModal(true)}
                  className="btn-primary-custom text-lg py-7 rounded-full font-semibold flex items-center justify-center gap-2"
                >
                  <Gavel size={20} />
                  Place a Bid
                </Button>
                <Link to="/enquiry" className="flex-1">
                  <Button 
                    data-testid="enquire-button" 
                    size="lg" 
                    variant="outline"
                    className="w-full text-lg py-7 rounded-full font-semibold border-2"
                    style={{ borderColor: '#d4a574', color: '#d4a574' }}
                  >
                    Enquire Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Description and Details */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Card className="rounded-2xl border-2" style={{ borderColor: '#e8e8e8' }}>
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold mb-6" style={{ color: '#1a1a2e' }}>The Space</h2>
                  <p className="text-lg leading-relaxed" style={{ color: '#636e72' }}>{property.description}</p>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-2" style={{ borderColor: '#e8e8e8' }}>
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Location & Access</h2>
                  <p className="text-lg leading-relaxed" style={{ color: '#636e72' }}>{property.location_description}</p>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-2" style={{ borderColor: '#e8e8e8' }}>
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Ideal For</h2>
                  <ul className="space-y-3">
                    {property.ideal_for.map((item, index) => (
                      <li key={index} className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition">
                        <CheckCircle size={24} className="mr-3 flex-shrink-0" style={{ color: '#d4a574' }} />
                        <span className="text-lg" style={{ color: '#2d3436', fontWeight: '500' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card className="sticky top-24 rounded-2xl border-2" style={{ borderColor: '#e8e8e8' }}>
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <p className="text-sm mb-2" style={{ color: '#636e72' }}>Standard Rate</p>
                    <p className="text-4xl font-bold mb-1" style={{ color: '#d4a574' }}>£{property.nightly_rate}</p>
                    <p className="text-sm" style={{ color: '#636e72' }}>per night</p>
                  </div>
                  
                  <div className="border-t border-b py-4 mb-6" style={{ borderColor: '#e8e8e8' }}>
                    <p className="text-center font-semibold mb-2" style={{ color: '#1a1a2e' }}>
                      Want a better rate?
                    </p>
                    <p className="text-sm text-center" style={{ color: '#636e72' }}>
                      Place a competitive bid and we'll review it promptly
                    </p>
                  </div>

                  <Button 
                    data-testid="sidebar-bid-button" 
                    onClick={() => setShowBiddingModal(true)}
                    className="w-full btn-primary-custom rounded-full py-6 text-lg font-semibold mb-3 flex items-center justify-center gap-2"
                  >
                    <Gavel size={20} />
                    Place Your Bid
                  </Button>

                  <p className="text-xs text-center mb-4" style={{ color: '#636e72' }}>or</p>

                  <h3 className="text-xl font-bold mb-3" style={{ color: '#1a1a2e' }}>Make an Enquiry</h3>
                  <p className="mb-4 leading-relaxed text-sm" style={{ color: '#636e72' }}>
                    Get in touch to discuss your requirements and availability options.
                  </p>
                  <Link to="/enquiry">
                    <Button 
                      data-testid="sidebar-enquire-button" 
                      variant="outline"
                      className="w-full rounded-full py-6 text-lg font-semibold border-2"
                      style={{ borderColor: '#d4a574', color: '#d4a574' }}
                    >
                      Contact Us
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertyDetailPage;